import { Routes } from "@angular/router";
import { loadTranslationResolver } from "@app/kernel/translations/resolvers/load-translations.resolver";
import { COMMON_GUARD_OPTIONS } from "@app/shared/auth/constants/permissions.constant";
import { AuthGuard } from "@app/shared/auth/guards/auth.guard";
import { TRANSLATION_PATHS } from "@app/shared/general/translation/constants/translation.constant";
import { VehicleBlockApiService } from "./domains/vehicle-block/services/vehicle-block-api.service";
import { VehicleBlockTypeApiService } from "./domains/vehicle-block-type/services/vehicle-block-type-api.service";
import { StepConfigurationApiService } from "./domains/step-configuration/services/step-configuration-api.service";
import { PhotoTypeApiService } from "./domains/photo-type/services/photo-type-api.service";
import { VehicleTransferApiService } from "./domains/vehicle-transfer/services/vehicle-transfer-api.service";
import { VehicleOnboardingRefreshService } from "@app/shared/onboarding/services/vehicle-onboarding-refresh.service";
import { MovementApiService } from "./domains/movements/services/movement-api.service";
import { ConfirmMovementSectionComponent } from "./domains/movements/components/confirm-movement-section/confirm-movement-section.component";
import { CONFIRM_SECTION_COMPONENT } from "./domains/movements/constants/movement.constants";
import { MovementOnboardingFlowService } from "./domains/movements/services/movement-onboarding-flow.service";
import { MovementDraftFlowService } from "./domains/movements/services/movement-draft-flow.service";
import { VehicleOnboardingHost } from "@app/shared/onboarding/services/vehicle-onboarding-host.abstraction";

export const movementsManagementRoutes: Routes = [
  {
    path: "movements",
    loadChildren: () => import("./domains/movements/movements.routes").then(r => r.movementRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.MOVEMENT.VIEW },
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.MOVEMENTS,
        TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.SHARED,
        TRANSLATION_PATHS.FLEET.VEHICLES,
        TRANSLATION_PATHS.FLEET.DAMAGES,
        TRANSLATION_PATHS.FLEET.VEHICLE_FILES,
        TRANSLATION_PATHS.ONBOARDING,
      ]),
    },
    // The flow service is also the onboarding dialog's host: the page and the dialog (a routed child) share it.
    // The draft flow hosts the dialog of the create and duplicate pages (their draft route overrides the host).
    providers: [
      MovementApiService,
      MovementOnboardingFlowService,
      MovementDraftFlowService,
      { provide: VehicleOnboardingHost, useExisting: MovementOnboardingFlowService },
      { provide: CONFIRM_SECTION_COMPONENT, useValue: ConfirmMovementSectionComponent },
    ],
  },
  {
    path: "vehicle-blocks",
    loadChildren: () => import("./domains/vehicle-block/vehicle-block.routes").then(r => r.vehicleBlocksRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.VEHICLE_BLOCK.VIEW },
    resolve: {
      translation: loadTranslationResolver([TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.VEHICLE_BLOCK, TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.SHARED]),
    },
    providers: [VehicleBlockApiService, VehicleBlockTypeApiService],
  },
  {
    path: "vehicle-block-type",
    loadChildren: () => import("./domains/vehicle-block-type/vehicle-block-type.routes").then(r => r.vehicleBlockTypeRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.VEHICLE_BLOCK_TYPE.VIEW },
    resolve: {
      translation: loadTranslationResolver([TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.VEHICLE_BLOCK_TYPE, TRANSLATION_PATHS.AUTHENTICATION.SHARED]),
    },
    providers: [VehicleBlockTypeApiService],
  },
  {
    path: "movement-type",
    loadChildren: () => import("./domains/movement-type/movement-type.routes").then(r => r.movementTypeRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.MOVEMENT_TYPE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.MOVEMENT_TYPE]) },
  },
  {
    path: "step-configuration",
    loadChildren: () => import("./domains/step-configuration/step-configuration.routes").then(r => r.stepConfigurationRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.STEP_CONFIGURATION.VIEW },
    resolve: {
      translation: loadTranslationResolver([TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.STEP_CONFIGURATION]),
    },
    providers: [StepConfigurationApiService],
  },
  {
    path: "photo-type",
    loadChildren: () => import("./domains/photo-type/photo-type.routes").then(r => r.photoTypeRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.PHOTO_TYPE.VIEW },
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.PHOTO_TYPE,
        TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.SHARED,
        TRANSLATION_PATHS.ONBOARDING,
      ]),
    },
    providers: [PhotoTypeApiService],
  },
  {
    path: "equipment",
    loadChildren: () => import("./domains/equipment/equipment.routes").then(r => r.equipmentRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.EQUIPMENT.VIEW },
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.EQUIPMENT,
        TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.SHARED,
        TRANSLATION_PATHS.ONBOARDING,
      ]),
    },
  },
  {
    path: "vehicle-transfer",
    loadChildren: () => import("./domains/vehicle-transfer/vehicle-transfer.routes").then(r => r.vehicleTransferRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.VEHICLE_TRANSFER.VIEW },
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.VEHICLE_TRANSFER,
        TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.SHARED,
        TRANSLATION_PATHS.FLEET.VEHICLES,
        TRANSLATION_PATHS.FLEET.DAMAGES,
        TRANSLATION_PATHS.FLEET.VEHICLE_FILES,
        TRANSLATION_PATHS.ONBOARDING,
      ]),
    },
    providers: [VehicleTransferApiService, VehicleOnboardingRefreshService],
  },
  {
    path: "reasons",
    loadChildren: () => import("./domains/reasons/reasons.routes").then(r => r.reasonsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.REASON_SETTINGS.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.MOVEMENTS_MANAGEMENT.REASONS]) },
  },
];
